import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faCircleInfo,
  faFilter,
  faRotateLeft,
  faSliders,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parseDateStr, formatDateStr } from "../utils";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "34px",
    fontSize: "0.8125rem",
    borderColor: state.isFocused ? "#144677" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #144677" : "none",
    "&:hover": { borderColor: state.isDisabled ? "#e5e7eb" : "#144677" },
    backgroundColor: state.isDisabled ? "#f3f4f6" : "white",
    cursor: state.isDisabled ? "not-allowed" : "default",
    pointerEvents: state.isDisabled ? "none" : "auto",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.8125rem",
    backgroundColor: state.isSelected
      ? "#144677"
      : state.isFocused
      ? "#e8f0fe"
      : "white",
  }),
  placeholder: (base) => ({ ...base, fontSize: "0.8125rem", color: "#9ca3af" }),
  singleValue: (base) => ({ ...base, fontSize: "0.8125rem" }),
  multiValue: (base) => ({ ...base, backgroundColor: "#e8f0fe" }),
  multiValueLabel: (base) => ({ ...base, color: "#144677", fontSize: "0.75rem" }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#144677",
    "&:hover": { backgroundColor: "#144677", color: "white" },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  dropdownIndicator: (base) => ({ ...base, padding: "0 6px" }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
};

export const initialFilters = {
  fromDate: "",
  toDate: "",
  kpiHit: false,            // null = Both, true = Yes, false = No
  isProcessed: false,       // null = Both, true = Yes, false = No
  completedOnly: false,
  parentCompanySearch: [],
  companySearch: [],
  carrierSearch: [],
  serviceSearch: [],
  trackingSearch: [],
  receiverSearch: [],
  kpiMeasureMin: "",
  kpiMeasureMax: "",
  // Advanced (modal)
  msNumber: "",
  connote: "",
  portfolio: [],
  reasonSearch: "",
  serviceFlag: "",
  fromStateSearch: [],
  toStateSearch: [],
};

const inputCls =
  "w-full px-3 py-[7px] text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#144677] focus:border-[#144677] placeholder:text-gray-400 transition-colors";

const formatCalendarWeekDay = (dayName) => dayName.slice(0, 3);

const yesterday = new Date();
yesterday.setDate(yesterday.getDate() - 1);

const advInitial = {
  msNumber: "",
  connote: "",
  portfolio: [],
  reasonSearch: "",
  serviceFlag: "",
  fromStateSearch: [],
  toStateSearch: [],
};

export default function FilterBar({
  filters,
  appliedFilters,
  filterOptionsData,
  isFilterOptionsLoading = false,
  areDependentFiltersDisabled = false,
  onFilterChange,
  onApplyFilters,
  onClearFilters,
  stats,
  difotPercentage = "0.0%",
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advDraft, setAdvDraft] = useState(advInitial);

  const set = (key, val) => onFilterChange({ ...filters, [key]: val });
  const setDate = (key) => (date) => set(key, date ? formatDateStr(date) : "");
  const hasPendingChanges =
    JSON.stringify(filters) !== JSON.stringify(appliedFilters);
  const hasCompleteDateRange = Boolean(filters.fromDate && filters.toDate);
  const canApplyFilters = hasPendingChanges || hasCompleteDateRange;

  const activeCount = Object.entries(appliedFilters).filter(([key, val]) => {
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === "string") return val.trim() !== "";
    // Booleans (completedOnly, kpiHit, isProcessed) only count once they move
    // away from their default, otherwise the badge never drops below 2.
    return val !== initialFilters[key] && val !== undefined;
  }).length;

  const advancedActiveCount =
    [
      filters.msNumber,
      filters.connote,
      filters.reasonSearch,
      filters.serviceFlag,
    ]
      .filter((v) => v && v.trim()).length +
    (filters.portfolio?.length > 0 ? 1 : 0) +
    (filters.fromStateSearch?.length > 0 ? 1 : 0) +
    (filters.toStateSearch?.length > 0 ? 1 : 0);
  const openAdvanced = () => {
    setAdvDraft({
      msNumber: filters.msNumber || "",
      connote: filters.connote || "",
      portfolio: filters.portfolio || [],
      reasonSearch: filters.reasonSearch || "",
      serviceFlag: filters.serviceFlag || "",
      fromStateSearch: filters.fromStateSearch || [],
      toStateSearch: filters.toStateSearch || [],
    });
    setShowAdvanced(true);
  };

  const applyAdvanced = () => {
    const newFilters = { ...filters, ...advDraft };
    onFilterChange(newFilters);
    setShowAdvanced(false);
  };

  const clearAdvanced = () => {
    const clearedFields = {
      msNumber: "",
      connote: "",
      portfolio: [],
      reasonSearch: "",
      serviceFlag: "",
      fromStateSearch: [],
      toStateSearch: [],
    };
    setAdvDraft(clearedFields);
    const newFilters = { ...filters, ...clearedFields };
    onFilterChange(newFilters);
    setShowAdvanced(false);
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-200 sticky top-0 z-30">
        {/* ── Header: identity, stats, actions ───────────────────────── */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 pt-3 pb-2.5">
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-7 h-7 rounded-lg bg-[#144677] flex items-center justify-center">
              <FontAwesomeIcon icon={faFilter} className="w-3 h-3 text-white" />
            </div>
            <span className="font-bold text-gray-800 text-sm tracking-wide">FILTERS</span>
            {activeCount > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-[#FF6266] text-white rounded-full">
                {activeCount}
              </span>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <StatBadge label="DIFOT %" value={difotPercentage} info />
            <StatBadge label="TOTAL" value={stats.total} />
            <StatBadge label="HIT" value={stats.hit} accent />
            <StatBadge label="MISS" value={stats.miss} danger />
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={onClearFilters}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faRotateLeft} className="w-3 h-3" />
              Reset Filters
            </button>
            <button
              onClick={() => setIsExpanded((p) => !p)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <FontAwesomeIcon
                icon={isExpanded ? faChevronUp : faChevronDown}
                className="w-3 h-3"
              />
              {isExpanded ? "Collapse" : "Expand"}
            </button>
          </div>
        </div>

        {/* ── Primary controls: always visible, one shared baseline ──── */}
        <div className="flex flex-wrap items-end gap-x-3 gap-y-2.5 px-4 pb-3">
          <Field label="Despatch Date Range">
            <div className="flex h-[34px] items-center gap-1 rounded-lg border border-gray-200 bg-white px-1.5 focus-within:border-[#144677] focus-within:ring-1 focus-within:ring-[#144677]">
              {/* Each picker keeps its own wrapper: index.css sizes the inner
                  input to 100% of it, so the widths live on these divs. */}
              <div className="difot-datepicker-wrapper w-[92px]">
                <DatePicker
                  selected={parseDateStr(filters.fromDate)}
                  onChange={setDate("fromDate")}
                  dateFormat="dd/MM/yyyy"
                  formatWeekDay={formatCalendarWeekDay}
                  placeholderText="dd/mm/yyyy"
                  className="bg-transparent px-1 text-xs text-gray-700 focus:outline-none"
                  maxDate={yesterday}
                  popperProps={{ strategy: "fixed" }}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
              </div>
              <span className="select-none text-xs text-gray-300">&ndash;</span>
              <div className="difot-datepicker-wrapper w-[92px]">
                <DatePicker
                  selected={parseDateStr(filters.toDate)}
                  onChange={setDate("toDate")}
                  dateFormat="dd/MM/yyyy"
                  formatWeekDay={formatCalendarWeekDay}
                  placeholderText="dd/mm/yyyy"
                  className="bg-transparent px-1 text-xs text-gray-700 focus:outline-none"
                  maxDate={yesterday}
                  popperProps={{ strategy: "fixed" }}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
              </div>
            </div>
          </Field>

          <span className="hidden h-[34px] w-px self-end bg-gray-100 sm:block" />

          <Field label="KPI Hit">
            <TriStateToggle
              value={filters.kpiHit}
              onChange={(value) => set("kpiHit", value)}
            />
          </Field>

          <Field label="Is Processed">
            <TriStateToggle
              value={filters.isProcessed}
              onChange={(value) => set("isProcessed", value)}
            />
          </Field>

          <span className="hidden h-[34px] w-px self-end bg-gray-100 sm:block" />

          <Field
            label="Completed and Reportable Only"
            info="Complete, Complete - Returned To Sender, Damaged, Lost OR Any Consignment that has been Processed"
          >
            <button
              type="button"
              onClick={() => set("completedOnly", !filters.completedOnly)}
              aria-pressed={filters.completedOnly}
              className={`flex h-[34px] w-full min-w-[86px] items-center gap-2 rounded-lg border px-2.5 text-xs font-semibold transition-colors ${
                filters.completedOnly
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              <span
                className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors ${
                  filters.completedOnly ? "bg-emerald-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                    filters.completedOnly ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </span>
              {filters.completedOnly ? "On" : "Off"}
            </button>
          </Field>
        </div>

        {/* ── Expanded filter rows ────────────────────────────────────── */}
        {isExpanded && (
          <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
            {/* Row 1 */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                  Parent Company
                </label>
                <Select
                  options={filterOptionsData.parentCompanies}
                  value={filters.parentCompanySearch}
                  onChange={(options) => set("parentCompanySearch", options || [])}
                  isMulti
                  isClearable
                  isLoading={isFilterOptionsLoading}
                  isDisabled={areDependentFiltersDisabled}
                  placeholder={
                    areDependentFiltersDisabled
                      ? "Select date range first"
                      : "All parent companies"
                  }
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                  Child Company
                </label>
                <Select
                  options={filterOptionsData.childCompanies}
                  value={filters.companySearch}
                  onChange={(options) => set("companySearch", options || [])}
                  isMulti
                  isClearable
                  isLoading={isFilterOptionsLoading}
                  isDisabled={areDependentFiltersDisabled}
                  placeholder={
                    areDependentFiltersDisabled
                      ? "Select date range first"
                      : "All child companies"
                  }
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                  Carrier
                </label>
                <Select
                  options={filterOptionsData.carriers}
                  value={filters.carrierSearch}
                  onChange={(options) => set("carrierSearch", options || [])}
                  isMulti
                  isClearable
                  isLoading={isFilterOptionsLoading}
                  isDisabled={areDependentFiltersDisabled}
                  placeholder={
                    areDependentFiltersDisabled
                      ? "Select date range first"
                      : "All carriers"
                  }
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                  Service
                </label>
                <Select
                  options={filterOptionsData.services}
                  value={filters.serviceSearch}
                  onChange={(options) => set("serviceSearch", options || [])}
                  isMulti
                  isClearable
                  isLoading={isFilterOptionsLoading}
                  isDisabled={areDependentFiltersDisabled}
                  placeholder={
                    areDependentFiltersDisabled
                      ? "Select date range first"
                      : "All services"
                  }
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                  Receiver
                </label>
                <Select
                  options={filterOptionsData.receivers}
                  value={filters.receiverSearch}
                  onChange={(options) => set("receiverSearch", options || [])}
                  isMulti
                  isClearable
                  isLoading={isFilterOptionsLoading}
                  isDisabled={areDependentFiltersDisabled}
                  placeholder={
                    areDependentFiltersDisabled
                      ? "Select date range first"
                      : "All receivers"
                  }
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                  Scan Events
                </label>
                <Select
                  options={filterOptionsData.trackingStatuses}
                  value={filters.trackingSearch}
                  onChange={(options) => set("trackingSearch", options || [])}
                  isMulti
                  isClearable
                  isLoading={isFilterOptionsLoading}
                  isDisabled={areDependentFiltersDisabled}
                  placeholder={
                    areDependentFiltersDisabled
                      ? "Select date range first"
                      : "All statuses"
                  }
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                  KPI Measure
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.kpiMeasureMin}
                    onChange={(e) => set("kpiMeasureMin", e.target.value)}
                    className={inputCls}
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.kpiMeasureMax}
                    onChange={(e) => set("kpiMeasureMax", e.target.value)}
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="flex items-end">
                <button
                  onClick={openAdvanced}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-[7px] text-xs font-semibold border rounded-lg transition-colors ${
                    advancedActiveCount > 0
                      ? "bg-[#144677] text-white border-[#144677]"
                      : "text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <FontAwesomeIcon icon={faSliders} className="w-3 h-3" />
                  Advanced
                  {advancedActiveCount > 0 && (
                    <span className="ml-0.5 px-1.5 py-0.5 text-[10px] font-bold bg-white/25 rounded-full">
                      {advancedActiveCount}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Apply button */}
            <div className="flex justify-end pt-1 border-t border-gray-100">
              <button
                onClick={() => onApplyFilters()}
                disabled={!canApplyFilters}
                className={`px-4 py-2 text-xs font-semibold rounded-lg transition-colors shadow-sm ${
                  canApplyFilters
                    ? "text-white bg-[#144677] hover:bg-[#1a5691]"
                    : "text-gray-400 bg-gray-100 cursor-not-allowed shadow-none"
                }`}
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Advanced Filters Modal ──────────────────────────────────── */}
      {showAdvanced && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
            onClick={() => setShowAdvanced(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header */}
              <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
                <div>
                  <h2 className="text-base font-bold text-gray-800">Advanced Filters</h2>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Refine your search with additional criteria
                  </p>
                </div>
                <button
                  onClick={() => setShowAdvanced(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
                </button>
              </div>

              {/* Modal body */}
              <div className="px-6 py-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Machship No
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., MS61717163"
                      value={advDraft.msNumber}
                      onChange={(e) => setAdvDraft((p) => ({ ...p, msNumber: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Connote No
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., CN12345678"
                      value={advDraft.connote}
                      onChange={(e) => setAdvDraft((p) => ({ ...p, connote: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Portfolio
                    </label>
                    <Select
                      options={filterOptionsData.portfolios}
                      value={advDraft.portfolio}
                      onChange={(val) => setAdvDraft((p) => ({ ...p, portfolio: val || [] }))}
                      isMulti
                      isClearable
                      isLoading={isFilterOptionsLoading}
                      isDisabled={
                        areDependentFiltersDisabled ||
                        (!isFilterOptionsLoading && !filterOptionsData.portfolios?.length)
                      }
                      placeholder={
                        areDependentFiltersDisabled
                          ? "Select date range first"
                          : isFilterOptionsLoading
                          ? "Loading…"
                          : !filterOptionsData.portfolios?.length
                          ? "No portfolios"
                          : "All"
                      }
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      From State
                    </label>
                    <Select
                      options={filterOptionsData.fromStates}
                      value={advDraft.fromStateSearch}
                      onChange={(options) =>
                        setAdvDraft((p) => ({ ...p, fromStateSearch: options || [] }))
                      }
                      isMulti
                      isClearable
                      isLoading={isFilterOptionsLoading}
                      isDisabled={areDependentFiltersDisabled}
                      placeholder="All from states"
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    Reason
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Weather Delay"
                    value={advDraft.reasonSearch}
                    onChange={(e) => setAdvDraft((p) => ({ ...p, reasonSearch: e.target.value }))}
                    className={inputCls}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Service Flag
                    </label>
                    <Select
                      options={filterOptionsData.serviceFlags}
                      value={
                        filterOptionsData.serviceFlags.find(
                          (option) => option.value === advDraft.serviceFlag
                        ) || null
                      }
                      onChange={(option) =>
                        setAdvDraft((p) => ({ ...p, serviceFlag: option?.value || "" }))
                      }
                      isClearable
                      isLoading={isFilterOptionsLoading}
                      isDisabled={areDependentFiltersDisabled}
                      placeholder="All service flags"
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      To State
                    </label>
                    <Select
                      options={filterOptionsData.toStates}
                      value={advDraft.toStateSearch}
                      onChange={(options) =>
                        setAdvDraft((p) => ({ ...p, toStateSearch: options || [] }))
                      }
                      isMulti
                      isClearable
                      isLoading={isFilterOptionsLoading}
                      isDisabled={areDependentFiltersDisabled}
                      placeholder="All to states"
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
              </div>

              {/* Modal footer */}
              <div className="flex items-center justify-end gap-3 px-6 pb-5">
                <button
                  onClick={clearAdvanced}
                  className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Clear
                </button>
                <button
                  onClick={applyAdvanced}
                  className="px-5 py-2 text-sm font-bold text-white bg-[#144677] hover:bg-[#1a5691] rounded-xl transition-colors shadow-sm"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function Field({ label, info, children }) {
  return (
    <div className="flex flex-col">
      <div className="mb-1 flex items-center gap-1">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
          {label}
        </p>
        {info && <InfoBubble text={info} />}
      </div>
      {children}
    </div>
  );
}

function InfoBubble({ text }) {
  return (
    <span className="group relative inline-flex" tabIndex={0}>
      <FontAwesomeIcon
        icon={faCircleInfo}
        className="h-3 w-3 text-gray-400 hover:text-gray-600"
      />
      <span className="pointer-events-none absolute left-1/2 top-full z-[9999] mt-1 hidden w-72 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-left text-[11px] font-medium normal-case leading-relaxed tracking-normal text-white shadow-lg group-hover:block group-focus:block">
        {text}
      </span>
    </span>
  );
}

const TRI_STATE_OPTIONS = [
  { label: "Yes", value: true },
  { label: "No", value: false },
  { label: "Both", value: null },
];

function TriStateToggle({ value, onChange }) {
  return (
    <div className="flex h-[34px] items-center overflow-hidden rounded-lg border border-gray-200">
      {TRI_STATE_OPTIONS.map(({ label, value: optionValue }, i) => (
        <button
          key={label}
          type="button"
          onClick={() => onChange(optionValue)}
          className={`h-full px-3 text-xs font-semibold transition-colors ${
            i > 0 ? "border-l border-gray-200" : ""
          } ${
            value === optionValue
              ? "bg-[#144677] text-white"
              : "text-gray-500 hover:bg-gray-50"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function StatBadge({ label, value, accent, danger, warn, info }) {
  const bg = info
    ? "bg-sky-600"
    : accent
    ? "bg-emerald-600"
    : danger
    ? "bg-red-500"
    : warn
    ? "bg-amber-500"
    : "bg-[#144677]";
  return (
    <div
      className={`${bg} text-white px-3 py-1.5 rounded-lg text-center min-w-[58px] shadow-sm`}
    >
      <div className="text-[9px] font-bold uppercase tracking-wider opacity-80">
        {label}
      </div>
      <div className="text-base font-bold leading-tight">{value}</div>
    </div>
  );
}
